import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { getClaimKey } from '../../../functions/get-claim-key';
import { ExaminerDiary } from '../../../models/api/view/claim/documentation/examiner-diary/examiner-diary';
import { Action } from '../../../models/core/action';
import { CasePipe } from '../../../pipes/case.pipe';
import { Format } from '../../../pipes/format.pipe';
import { ExportService } from '../../../services/export/export.service';
import { DiaryExportMapper } from '../../../utilities/diary-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { WatchButtonComponent } from '../../core/buttons/watch-button.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { ExpandPanelComponent } from '../../core/panels/expand-panel.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { getExpandSubject } from '../../../functions/get-expand-subject';

@Component({
  selector: 'app-diary-table-accommodation',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    CommonModule,
    ReactiveFormsModule,
    LoadingPanelComponent,
    ExpandPanelComponent,
    WatchButtonComponent,
    EmptyStateComponent,
    CasePipe,
    HighlightPipe,
  ],
  template: `
    <app-loading-panel [state]="state"
      ><ng-template #loaded>
        <app-table
          #table
          [dataSource]="filteredDiary"
          [pageSize]="10"
          [showSelection]="showSelection"
          [showExport]="true"
          [expandable]="true"
          [scrollable]="false"
          (configure)="showPickList = true"
          (selectedEvent)="onSelection($event)"
          recordLabel="diary"
          recordLabelPlural="diaries"
          [ariaTableLabel]="'Diaries'"
          filterPlaceholder="Search diary text"
          [showFilterButton]="false"
          [globalFilterFields]="['text']"
          (export)="onExport($event)"
          [sortField]="'dueDate'"
          [sortOrder]="1"
        >
          <app-column
            name="dueDate"
            label="Due Date"
            format="d"
            filterType="date"
          />
          <app-column name="examiner" label="Examiner" filterType="text" />
          <app-column name="type" label="Diary Type" filterType="select" />
          <app-column name="claim" label="Claim" filterType="text">
            <ng-template let-data>
              {{ data.claim }}
              <app-watch-button
                [recordSource]="claim.recordSource"
                [recordId]="claim.recordId"
              />
            </ng-template>
          </app-column>
          <app-column name="text" [fullRow]="true" [fullRowLabel]="false">
            <ng-template let-data>
              <app-expand-panel
                [expanded]="table.expanded"
                [label]="getExpandSubject(data.claim, data.type) || 'Diary'"
                collapseHeight="80px"
              >
                {{ data.text | highlight: table.searchValue }}
              </app-expand-panel>
            </ng-template>
          </app-column>
        </app-table></ng-template
      ><ng-template #empty>
        <app-empty-state
          [icon]="'fa-table fa-light'"
          [label]="'No diaries' | case: 'sentence'"
        />
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class DiaryTableAccommodationComponent
  extends BaseComponent
  implements OnInit
{
  private datePipe = inject(DatePipe);
  exportService = inject(ExportService);

  protected getExpandSubject = getExpandSubject;

  [x: string]: any;
  @Output() searchCount = new EventEmitter<number>();

  @Input()
  showDateAccessed = false;

  @Input()
  showSelection = false;

  private _filteredStatus = 'Open';

  @Input()
  set filteredStatus(value: string) {
    this._filteredStatus = value;
    if (this.diaryData?.length > 0) {
      this.completeOpen(value);
    }
  }

  get filteredStatus(): string {
    return this._filteredStatus;
  }

  private _diaryData: ExaminerDiary[];

  @Input()
  set diaryData(value: ExaminerDiary[]) {
    this._diaryData = value;
    if (value?.length > 0 && this.filteredStatus) {
      this.completeOpen(this.filteredStatus);
    }
  }

  get diaryData(): ExaminerDiary[] {
    return this._diaryData;
  }
  @ViewChild('table')
  table: TableComponent;
  @ViewChildren(ColumnComponent)
  columnsList: QueryList<ColumnComponent>;

  claim: any = null;
  showPickList = false;
  loaded = false;
  disableMarkComplete = true;
  error: any = null;
  types: any[] = [];
  protected state: LoadingState = 'Loading';
  codes: any[] = [];
  filteredDiary: ExaminerDiary[] = [];

  actions: Action[] = [
    { label: 'Mark complete', key: 'complete' },
    { label: 'Edit', key: 'edit' },
  ];

  async ngOnInit() {
    if (document.location.pathname !== '/todo') {
      this.claim = this.view.currentClaim();
    }
    const claimKey = getClaimKey();
    const item = await this.api.examinerDiary.get(claimKey, null);
    this.diaryData = item.examinerDiaries;
    this.codes = item.codes;
    if (this.diaryData.length > 0) {
      this.state = 'Loaded';
    } else {
      this.state = 'Empty';
    }
    this.completeOpen(this.filteredStatus);
    this.searchCount.emit(this.diaryData.length);
  }

  getBackData() {
    const data = {
      backRouterLink: document.location.pathname,
      backLabel:
        document.location.pathname === '/todo' ? 'My to-do' : 'Examiner diary',
    };
    return data;
  }

  onSelection(selections: any[]) {
    this.disableMarkComplete = selections.length === 0;
  }

  getFormat(name: string): Format {
    switch (name) {
      case 'DateAccessed':
        return 'dt';
      case 'PtdTotal':
        return 'c';
      case 'DateClosed':
      case 'DateBegin':
      case 'DateEnd':
      case 'DateBirth':
      case 'DateOpen':
        return 'd';
      default:
        return null;
    }
  }
  completeOpen(status: string) {
    if (!this.diaryData || this.diaryData.length === 0) {
      this.filteredDiary = [];
      return;
    }
    this.filteredDiary = this.diaryData.filter(
      (diary) => diary.status === status,
    );
  }

  async onExport(format: ExportFormat) {
    try {
      const rows = this.table?.filteredValue
        ? this.table.filteredValue
        : this.table?.dataSource;
      const columns = this.columnsList.toArray();
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `Diary_${formattedDate}.${format}`;

      if (format === 'csv') {
        const csvData = DiaryExportMapper.toCsvExport(rows, columns, 'Diary');
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = DiaryExportMapper.toExcelExport(
          rows,
          columns,
          'Diary',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (error) {
      this.error = error;
    }
  }
}
